import React, { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./styles.module.scss";
import PaymentsTable from "../../Billing/Customers/CustomerDetails/PaymentTable";
import SubscriptionCardWithCoach from "../../Billing/Customers/CustomerDetails/SubscriptionCardWithCoach";
import AccountWrap from "..";
import { schedulingFetchGet } from "app/utils/request/scheduling/fetch";
import { useSelector } from "react-redux";
import { RootState } from "typedefs";
import { useTranslation } from "react-i18next";
import { cls } from "app/utils";
import { CreditBundle } from "app/models/scheduling/CreditBundleModel";
import { scheduledOfferingsFromPurchases, transformCreditBundlesToOfferings } from "app/utils/creditBundles";
import PendingPurchasesList from "app/components/Programs/PendingPurchasesList";
import { useBookingParticipants } from "app/routes/Sessions/useBookingParticipants";
import { FamilyMember } from "app/routes/Account/FamilyEdit/types";

interface RateLink {
  title: string;
  quantity: number;
  duration: string;
  remainingSessions: number;
  unlimited: boolean;
}

interface Subscription {
  fullName?: string;
  profilePicture: string;
  offeringTitle: string;
  billingFrequency: number | null;
  nextInvoiceDate: string | null;
  // CN-2641: end date of a capped subscription run (null when uncapped).
  subscriptionEndsAt?: string | null;
  expiredAt?: string | null;
  productPrice: number;
  currency: string;
  remainingSessions: number | null;
  unlimited?: boolean;
  productType?: string;
  productId?: string;
  coachId?: string;
  ownerType?: "Facility" | "CoachProfile";
  studentProfileId?: string;
  rateLinks?: RateLink[];
  cancelled?: boolean;
  // CN-1323 (CFTB-3304): which family member this offering belongs to, so a
  // head sees "Summer Pack — Emma". Unset for self/solo accounts.
  familyMemberName?: string | null;
  // CN-2832: signed up before a custom start date; the first charge is on it.
  billingStartsAt?: string | null;
}

const ManagePayments: React.FC = () => {
  const { t } = useTranslation();
  const [payments, setPayments] = useState([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  // CN-2832: a subscription waiting for its custom start date has no credits
  // yet, so it comes from payment history rather than the credit bundles.
  const displayedSubscriptions = useMemo<Subscription[]>(
    () => [
      ...subscriptions,
      ...scheduledOfferingsFromPurchases(payments).map((offering) => ({
        ...offering,
        profilePicture: offering.profilePicture || "",
        remainingSessions: offering.remainingSessions as number | null,
        familyMemberName: offering.studentFullName ?? null,
      })),
    ],
    [subscriptions, payments],
  );
  const [isLoadingPayments, setIsLoadingPayments] = useState(true);
  const [isLoadingSubscriptions, setIsLoadingSubscriptions] = useState(true);
  const [errorPayments, setErrorPayments] = useState<string | null>(null);
  const [errorSubscriptions, setErrorSubscriptions] = useState<string | null>(
    null,
  );
  const userId = useSelector((state: RootState) => state.user.self.id);
  // CN-1323 (CFTB-3286): only family accounts see which member each payment is
  // for; solo accounts keep the plain history.
  const { hasFamily, familyMembers } = useBookingParticipants();

  const loadPayments = useCallback(
    () =>
      schedulingFetchGet(`/students/${userId}/all_purchases`).then((response) => {
        if (!response) {
          throw new Error("Unexpected response format");
        }
        setPayments(response);
      }),
    [userId],
  );

  useEffect(() => {
    setIsLoadingPayments(true);
    setErrorPayments(null);

    loadPayments()
      .catch((err) => {
        console.error("Failed to fetch payments:", err);
        setErrorPayments(err.message || t("Failed to fetch payments"));
      })
      .finally(() => {
        setIsLoadingPayments(false);
      });
  }, [loadPayments, t]);

  useEffect(() => {
    setIsLoadingSubscriptions(true);
    setErrorSubscriptions(null);

    // CN-1323 (CFTB-3304): a package's credits are owned by the consumer, so a
    // head's own `/credit_bundles` never returns a dependent's package. Mirror
    // the Sessions "all" union (Sessions/index.tsx): fetch the head's bundles
    // plus each managed member's (scoped by `student_id`, authorized on the BE),
    // labeling each offering with the member it belongs to so the head sees
    // "Summer Pack — Emma".
    const fetchBundles = (member?: FamilyMember) =>
      schedulingFetchGet<CreditBundle>(
        "/credit_bundles",
        member ? { student_id: member.userId } : {},
      ).then((response) => {
        const bundles = (response as CreditBundle[]) || [];
        const offerings = transformCreditBundlesToOfferings(bundles);
        const memberName = member
          ? `${member.firstName} ${member.lastName}`.trim()
          : null;
        return offerings.map((o) => ({
          ...o,
          profilePicture: o.profilePicture || "",
          remainingSessions: o.remainingSessions as number | null,
          familyMemberName: memberName,
        }));
      });

    Promise.all([
      fetchBundles(),
      ...familyMembers.map((member) => fetchBundles(member)),
    ])
      .then((lists) => {
        setSubscriptions(lists.flat());
      })
      .catch((err) => {
        console.error("Failed to fetch subscriptions:", err);
        setErrorSubscriptions(
          err.message || t("Failed to fetch subscriptions"),
        );
      })
      .finally(() => {
        setIsLoadingSubscriptions(false);
      });
  }, [familyMembers, t]);

  const formatInvoiceDate = (isoDate: string | null) => {
    if (!isoDate) {
      return t("N/A");
    }
    const date = new Date(isoDate);
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
    }).format(date);
  };

  const handleDelete = (productId: string) => {
    // CN-2832: a scheduled card and its payment row both come from payment
    // history, so load it again: the card goes away and the row reads
    // "Cancelled", as after a page reload.
    if (displayedSubscriptions.some((sub) => sub.productId === productId && sub.billingStartsAt)) {
      loadPayments().catch((err) => console.error("Failed to fetch payments:", err));
      return;
    }
    setSubscriptions((prevSubscriptions) =>
      prevSubscriptions.map((subscription) =>
        subscription.productId === productId
          ? { ...subscription, nextInvoiceDate: null, cancelled: true }
          : subscription,
      ),
    );
  };

  const NoPayments = () => {
    return (
      <div className={styles.no_subscription_container}>
        <p>{t("No payments yet")}</p>
      </div>
    );
  };

  const renderPayments = useCallback(() => {
    if (payments.length === 0 && !isLoadingSubscriptions) {
      return <NoPayments />;
    }
    return <PaymentsTable payments={payments} showFamilyMember={hasFamily} />;
  }, [payments, isLoadingSubscriptions, hasFamily]);

  return (
    <AccountWrap>
      <div className={styles.container}>
        <h2 className={styles.title}>{t("Manage Payments")}</h2>

        <PendingPurchasesList
          wrapContent={(content) => {
            return (
              <>
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>{t("Pending Purchases")}</h3>
                  <hr className={styles.horizontal_line} />
                  {content}
                </div>
                <hr className={cls(styles.horizontal_line, styles.second_line)} />
              </>
            );
          }}
        />

        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>{t("Purchases")}</h3>
          <hr className={styles.horizontal_line} />
          {isLoadingSubscriptions ? (
            <p>{t("Loading subscriptions...")}</p>
          ) : errorSubscriptions ? (
            <p className={styles.errorText}>{errorSubscriptions}</p>
          ) : displayedSubscriptions.length > 0 ? (
            <div className={styles.subscriptionMargin}>
              {displayedSubscriptions.map((subscription, index) => (
                <SubscriptionCardWithCoach
                  key={index}
                  subscription={subscription}
                  coachName={subscription?.fullName || ""}
                  coachImage={subscription.profilePicture}
                  ownerType={subscription.ownerType}
                  familyMemberName={hasFamily ? subscription.familyMemberName : null}
                  formatInvoiceDate={formatInvoiceDate}
                  handleDelete={handleDelete}
                />
              ))}
            </div>
          ) : (
            <div className={styles.no_subscription_container}>
              <p>
                {t(
                  "You don't have any active subscription or purchased products.",
                )}
              </p>
            </div>
          )}
        </div>
        <hr className={cls(styles.horizontal_line, styles.second_line)} />

        {/* Payments Section */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>{t("Payments History")}</h3>
          {isLoadingPayments ? (
            <p>{t("Loading payments...")}</p>
          ) : errorPayments ? (
            <p className={styles.errorText}>{errorPayments}</p>
          ) : (
            renderPayments()
          )}
        </div>
      </div>
    </AccountWrap>
  );
};

export default ManagePayments;
